import { useFeature, useSetFacilityAddOn } from '../modules/add-ons/index.js'
import { useMe } from '../modules/accounts/index.js'
import { ApiError, Skeleton } from '../modules/core/index.js'
import { Switch } from '../modules/core/index.js'
import type { SessionRole } from '../modules/core/index.js'
import { useFeatures, type FeatureWebModule, type RosterContext } from '../modules/core/index.js'
import { RetentionReportCard } from '../modules/roster/index.js'
import { StaffingReportCard, WorkforcePlanCard } from '../modules/schedule/index.js'
import { RankingList, useFacilityRankings } from '../modules/partner/index.js'
import { useWorkerRoster } from '../modules/roster/index.js'
import { LoadFailure } from '../modules/core/index.js'
import { useMemo } from 'react'

interface ReportingProps {
  facilityId: string
  role: SessionRole
  /** Where this facility's Configuration is, for an add-on's advice about its switch. */
  configHref: string
}

/**
 * One reporting add-on's switch for this facility, at the top of the tab
 * it governs: an operator turns it on or off here (the same switch as
 * Configuration › Add-ons); a facility administrator sees which it is.
 * Nothing shows for an add-on the client's agreement does not include.
 */
function ReportingSwitch({ feature, facilityId, clientId, canToggle }: { feature: FeatureWebModule; facilityId: string; clientId: string | undefined; canToggle: boolean }) {
  const standing = useFeature(facilityId, feature.key)
  const setAddOn = useSetFacilityAddOn(clientId, facilityId)
  if (standing.isLoading || !standing.licensed) {
    return null
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-marine/20 bg-white px-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-black text-ink">{feature.name}</div>
        <div className="text-[11px] text-ink-mid">
          {standing.enabled ? 'On for this facility.' : 'Off for this facility. What was already generated stays readable.'}
          {!canToggle && ' Your operator switches it.'}
        </div>
        {setAddOn.isError && (
          <div role="alert" className="mt-1 text-[11px] text-coral">
            {setAddOn.error instanceof ApiError ? setAddOn.error.message : 'The switch could not be changed. Try again.'}
          </div>
        )}
      </div>
      <Switch checked={standing.enabled} onChange={(enabled) => setAddOn.mutate({ key: feature.key, enabled })} label={`${feature.name} for this facility`} disabled={!canToggle || setAddOn.isPending || clientId === undefined} />
    </div>
  )
}

/**
 * The facility's Reporting tab: its retention report (hires and leavers
 * by month, opening with its own summary tiles) and what it hands to
 * other systems,
 * one section per add-on that contributes one (src/features/index.ts),
 * such as Performance Summaries' export for HR. Each section gates
 * itself, so a switched-off add-on shows its own one-line note. A client
 * whose agreement includes no reporting add-on is told what there is.
 */
export function Reporting({ facilityId, role, configHref }: ReportingProps) {
  const me = useMe()
  const context: RosterContext = { facilityId, role, addOnsHref: configHref }
  const reporting = useFeatures().filter((feature) => feature.reportingSection)
  const licensed = reporting.filter((feature) => me.data?.features?.[feature.key]?.licensed)

  return (
    <div className="max-w-5xl mx-auto p-6 flex flex-col gap-8">
      {/* A core report, not an add-on: hires and leavers from the roster's own dates. */}
      <RetentionReportCard facilityId={facilityId} />
      <StaffingReportCard facilityId={facilityId} />
      <WorkforcePlanCard facilityId={facilityId} />
      <PartnerRankingsCard facilityId={facilityId} />
      {reporting.map((feature) => (
        <ReportingSwitch key={`switch-${feature.key}`} feature={feature} facilityId={facilityId} clientId={me.data?.clientId} canToggle />
      ))}
      {reporting.map((feature) => (feature.reportingSection ? <feature.reportingSection key={feature.key} {...context} /> : null))}
      {me.isSuccess && licensed.length === 0 && (
        <div className="bg-white border border-marine/15 rounded p-5">
          <div className="text-sm font-black text-ink mb-1">Reports are add-ons</div>
          <p className="text-xs text-ink-mid">
            {reporting.map((feature) => feature.name).join(', ') || 'None is available yet'}
            {reporting.length > 0 && ' hands a worker’s closed months to your HR system as a summary, a spreadsheet row or a printed document. Ask SeeWhoCares to add it to your agreement.'}
          </p>
        </div>
      )}
    </div>
  )
}

/** The partner's latest rankings at the facility (moved from the Dashboard, facility operations dashboard plan D8): what a scheduling partner asked to be ordered, and the order it got back. */
function PartnerRankingsCard({ facilityId }: { facilityId: string }) {
  const rankings = useFacilityRankings(facilityId)
  const roster = useWorkerRoster(facilityId)
  const names = useMemo(() => new Map((roster.data ?? []).map((worker) => [worker.workerId, worker.name])), [roster.data])
  return (
    <section>
      <div className="text-[10px] font-bold uppercase tracking-wide text-marine border-b border-marine/15 pb-2 mb-3">Partner rankings — most recent</div>
      {rankings.isPending ? (
        <Skeleton label="Loading rankings" />
      ) : rankings.isError ? (
        <LoadFailure size="xs" message="Could not load this facility’s rankings." onRetry={() => void rankings.refetch()} retrying={rankings.isFetching} />
      ) : (
        <RankingList decisions={rankings.data} names={names} />
      )}
    </section>
  )
}
